import React, { createContext, useContext, useEffect, useState } from 'react'

import { I18n } from 'aws-amplify/utils'
import { useTranslation } from 'react-i18next'

type Language = 'kr' | 'en'

interface LanguageContextType {
  isKorean: boolean
  toggleLanguage: () => void
  setLanguage: (language: 'kr' | 'en') => void
  authKey: number
}

const LanguageContext = createContext<LanguageContextType>({
  isKorean: false,
  toggleLanguage: () => {},
  setLanguage: () => {},
  authKey: 0,
})

const detectSystemLanguage = (): Language => {
  const userLanguages = navigator.languages || [navigator.language || 'en']
  const isKorean = userLanguages.some((lang) =>
    lang.toLowerCase().startsWith('ko')
  )
  return isKorean ? 'kr' : 'en'
}

const getInitialLanguage = (): Language => {
  try {
    const savedLanguage = localStorage.getItem('selectedLanguage')
    if (savedLanguage === 'kr' || savedLanguage === 'en') {
      return savedLanguage as Language
    }
  } catch (error) {
    console.warn('Failed to access localStorage:', error)
  }
  return detectSystemLanguage()
}

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isKorean, setIsKorean] = useState(() => getInitialLanguage() === 'kr')
  const [authKey, setAuthKey] = useState(0)
  const { i18n: reactI18n } = useTranslation()

  useEffect(() => {
    const language = getInitialLanguage()
    localStorage.setItem('selectedLanguage', language)
    I18n.setLanguage(language)
    reactI18n.changeLanguage(language)
    setIsKorean(language === 'kr')
  }, [])

  const toggleLanguage = () => {
    const newLanguage = isKorean ? 'en' : 'kr'
    try {
      I18n.setLanguage(newLanguage)
      reactI18n.changeLanguage(newLanguage)
      localStorage.setItem('selectedLanguage', newLanguage)
    } catch (error) {
      console.error('Failed to change language:', error)
      return // Don't update state if external calls failed
    }
    setIsKorean(!isKorean)
  }

  const setLanguage = (language: 'kr' | 'en') => {
    const newIsKorean = language === 'kr'
    if (isKorean !== newIsKorean) {
      try {
        I18n.setLanguage(language)
        reactI18n.changeLanguage(language)
        localStorage.setItem('selectedLanguage', language)
      } catch (error) {
        console.error('Failed to change language:', error)
        return // Don't update state if external calls failed
      }
      setIsKorean(newIsKorean)
    }
  }

  return (
    <LanguageContext.Provider value={{ isKorean, toggleLanguage, setLanguage, authKey }}>
      {children}
    </LanguageContext.Provider>
  )
}

export const useLanguage = () => useContext(LanguageContext)
